import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, Pause, Play, Clock, ListChecks, Timer } from "lucide-react";

const RING_RADIUS = 54;
const TICK_MS = 1000;
const AUTO_ADVANCE_DELAY_MS = 800;

function fmt(s) {
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, "0")}`;
}

function useStepTimer({ running, stepKey, seconds, onElapsed }) {
  const [remaining, setRemaining] = useState(seconds);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    setRemaining(seconds);
  }, [stepKey, seconds, running]);

  useEffect(() => {
    if (!running || paused || remaining <= 0) return;
    const t = setTimeout(() => setRemaining((r) => r - 1), TICK_MS);
    return () => clearTimeout(t);
  }, [running, paused, remaining]);

  useEffect(() => {
    if (!running || remaining > 0) return;
    const t = setTimeout(onElapsed, AUTO_ADVANCE_DELAY_MS);
    return () => clearTimeout(t);
  }, [running, remaining, onElapsed]);

  const togglePause = () => setPaused((p) => !p);
  return { remaining, paused, togglePause };
}

function TimerRing({ total, remaining, paused }) {
  const C = 2 * Math.PI * RING_RADIUS;
  const frac = total > 0 ? remaining / total : 0;
  return (
    <div className="relative w-32 h-32" data-testid="guided-timer-ring">
      <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
        <circle cx="60" cy="60" r={RING_RADIUS} fill="none" strokeWidth="4" className="stroke-border" />
        <circle cx="60" cy="60" r={RING_RADIUS} fill="none" strokeWidth="4" strokeLinecap="round"
          className="stroke-accent transition-[stroke-dashoffset] duration-1000 ease-linear"
          strokeDasharray={C} strokeDashoffset={C * (1 - frac)} />
      </svg>
      <span className={`absolute inset-0 flex items-center justify-center font-heading text-2xl ${paused ? "opacity-50" : ""}`} data-testid="guided-timer-value">
        {fmt(remaining)}
      </span>
    </div>
  );
}

function StepProgress({ steps, step }) {
  return (
    <div className="flex gap-1.5" data-testid="guided-progress-bar">
      {steps.map((s, i) => (
        <div key={s.label} className={`h-0.5 flex-1 rounded-full transition-colors duration-500 ${i <= step ? "bg-primary" : "bg-border"}`} />
      ))}
    </div>
  );
}

function StepContent({ step, index, count }) {
  return (
    <div key={index} className="animate-fade-up min-h-[36vh]" data-testid="guided-step-content">
      <p className="label-overline text-accent mb-6">{step.label} · {index + 1} of {count}</p>
      {step.type === "scripture" ? (
        <blockquote className="font-scripture italic text-2xl sm:text-3xl leading-relaxed text-primary">{step.instruction}</blockquote>
      ) : (
        <p className="font-heading text-2xl sm:text-3xl leading-snug text-foreground">{step.instruction}</p>
      )}
    </div>
  );
}

function DoneScreen({ practice, streak, navigate }) {
  return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <div className="text-center max-w-lg animate-fade-up" data-testid="guided-done-screen">
        <span className="w-16 h-16 rounded-full bg-primary text-primary-foreground flex items-center justify-center mx-auto mb-8 animate-breathe">
          <Check className="w-7 h-7" />
        </span>
        <h1 className="font-heading text-4xl sm:text-5xl tracking-tight">Well done, faithful one</h1>
        <p className="font-scripture italic text-xl text-primary/80 mt-5 leading-relaxed">"{practice.verse}"</p>
        <p className="text-sm text-muted-foreground mt-2">{practice.verse_ref}</p>
        {streak > 0 && <p className="text-sm text-muted-foreground mt-4">Day {streak} of your journey.</p>}
        <div className="flex gap-4 justify-center mt-10">
          <button data-testid="guided-journal-button" onClick={() => navigate("/journal")}
            className="bg-primary text-primary-foreground rounded-sm px-6 py-3 hover:bg-primary/90 transition-colors">Journal this</button>
          <button data-testid="guided-back-to-practices" onClick={() => navigate("/practices")}
            className="border border-border rounded-sm px-6 py-3 hover:bg-muted transition-colors">All practices</button>
        </div>
      </div>
    </div>
  );
}

function IntroScreen({ practice, onBegin, onRead, navigate }) {
  return (
    <div className="max-w-2xl mx-auto px-6 py-12 md:py-20">
      <button data-testid="guided-back-button" onClick={() => navigate("/practices")}
        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-12">
        <ArrowLeft className="w-4 h-4" /> Practices
      </button>
      <div className="animate-fade-up">
        <p className="label-overline mb-3">{practice.tagline}</p>
        <h1 className="font-heading text-4xl sm:text-5xl tracking-tight leading-none">{practice.name}</h1>
        <p className="text-muted-foreground mt-5 leading-relaxed max-w-xl">{practice.description}</p>
        <blockquote className="font-scripture italic text-xl text-primary/80 mt-8 leading-relaxed">"{practice.verse}"</blockquote>
        <p className="text-sm text-muted-foreground mt-2">{practice.verse_ref}</p>
        <div className="flex items-center gap-2 text-sm text-muted-foreground mt-8">
          <Clock className="w-4 h-4 text-accent" /> About {practice.duration_min} minutes · {practice.steps.length} movements
        </div>
        <div className="flex flex-wrap gap-4 mt-10">
          <button data-testid="guided-begin-button" onClick={onBegin}
            className="flex items-center gap-2 bg-primary text-primary-foreground rounded-sm px-7 py-3.5 hover:bg-primary/90 transition-colors">
            <Timer className="w-4 h-4" /> Begin guided session
          </button>
          <button data-testid="guided-read-mode-button" onClick={onRead}
            className="flex items-center gap-2 border border-border rounded-sm px-7 py-3.5 hover:bg-muted transition-colors">
            <ListChecks className="w-4 h-4" /> Read as a simple guide
          </button>
        </div>
      </div>
    </div>
  );
}

function ReadScreen({ practice, onBack, onComplete }) {
  return (
    <div className="max-w-2xl mx-auto px-6 py-12 md:py-20">
      <button data-testid="guided-read-back-button" onClick={onBack}
        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-12">
        <ArrowLeft className="w-4 h-4" /> {practice.name}
      </button>
      <p className="label-overline mb-2">{practice.tagline}</p>
      <h1 className="font-heading text-4xl tracking-tight leading-none mb-12">{practice.name}</h1>
      <ol className="space-y-10" data-testid="guided-read-steps">
        {practice.steps.map((s, i) => (
          <li key={s.label} className="animate-fade-up" style={{ animationDelay: `${i * 60}ms` }}>
            <p className="label-overline text-accent mb-3">{i + 1} · {s.label} <span className="text-muted-foreground normal-case tracking-normal ml-2">~{fmt(s.seconds)}</span></p>
            {s.type === "scripture" ? (
              <blockquote className="font-scripture italic text-xl text-primary leading-relaxed">{s.instruction}</blockquote>
            ) : (
              <p className="leading-relaxed text-foreground">{s.instruction}</p>
            )}
          </li>
        ))}
      </ol>
      <button data-testid="guided-read-complete-button" onClick={onComplete}
        className="flex items-center gap-2 bg-primary text-primary-foreground rounded-sm px-7 py-3.5 hover:bg-primary/90 transition-colors mt-14">
        <Check className="w-4 h-4" /> Mark practice complete
      </button>
    </div>
  );
}

export default function GuidedPractice() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [practice, setPractice] = useState(null);
  const [mode, setMode] = useState("intro");
  const [step, setStep] = useState(0);
  const [streak, setStreak] = useState(0);
  const completing = useRef(false);

  useEffect(() => {
    api.get(`/guided/${slug}`).then((r) => setPractice(r.data)).catch(() => navigate("/practices"));
  }, [slug, navigate]);

  const steps = practice?.steps || [];
  const current = steps[step];
  const isLast = step === steps.length - 1;

  const onElapsed = useCallback(() => {
    if (!isLast) setStep((s) => s + 1);
  }, [isLast]);

  const { remaining, paused, togglePause } = useStepTimer({
    running: mode === "guided" && !!current,
    stepKey: step,
    seconds: current?.seconds ?? 0,
    onElapsed,
  });

  const complete = async () => {
    if (completing.current) return;
    completing.current = true;
    try {
      const { data } = await api.post(`/guided/${slug}/complete`);
      setStreak(data.streak);
      setMode("done");
      toast.success("Practice complete. Peace be with you.");
    } catch {
      completing.current = false;
      toast.error("Could not save your completion.");
    }
  };

  if (!practice) return null;
  if (mode === "done") return <DoneScreen practice={practice} streak={streak} navigate={navigate} />;
  if (mode === "intro") {
    return <IntroScreen practice={practice} navigate={navigate}
      onBegin={() => { setStep(0); setMode("guided"); }} onRead={() => setMode("read")} />;
  }
  if (mode === "read") {
    return <ReadScreen practice={practice} onBack={() => setMode("intro")} onComplete={complete} />;
  }

  return (
    <div className="max-w-2xl mx-auto px-6 py-12 md:py-20">
      <button data-testid="guided-session-exit-button" onClick={() => setMode("intro")}
        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-10">
        <ArrowLeft className="w-4 h-4" /> {practice.name}
      </button>

      <div className="mb-14">
        <StepProgress steps={steps} step={step} />
      </div>

      <StepContent step={current} index={step} count={steps.length} />

      <div className="flex flex-col sm:flex-row items-center justify-between gap-8 mt-14">
        <div className="flex items-center gap-5">
          <TimerRing total={current.seconds} remaining={remaining} paused={paused} />
          <button data-testid="guided-pause-button" onClick={togglePause}
            className="w-11 h-11 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">
            {paused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
          </button>
        </div>
        <div className="flex items-center gap-6">
          <button data-testid="guided-prev-button" disabled={step === 0} onClick={() => setStep((s) => s - 1)}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors disabled:opacity-0">Previous</button>
          <button data-testid="guided-next-button" onClick={() => (isLast ? complete() : setStep((s) => s + 1))}
            className="flex items-center gap-2 bg-primary text-primary-foreground rounded-sm px-7 py-3.5 hover:bg-primary/90 transition-colors">
            {isLast ? "Complete practice" : "Continue"} <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
